import { useEffect, useState, type FormEvent } from "react";

type Balance = {
  balance_usd: string;
  reserved_usd: string;
  available_usd: string;
  billing_review_required: boolean;
  topup_min_usd: string;
  topup_max_usd: string;
};

export function ApiCredits() {
  const [balance, setBalance] = useState<Balance | null>(null);
  const [amount, setAmount] = useState("10.00");
  const [attempt, setAttempt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function refresh() {
    try {
      const response = await fetch("/api/v1/api-credits");
      if (!response.ok)
        throw new Error("Your API credit balance could not be loaded.");
      setBalance(await response.json());
      setError("");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Please refresh your balance.",
      );
    }
  }
  useEffect(() => {
    void refresh();
  }, []);

  async function fund(event: FormEvent) {
    event.preventDefault();
    const attemptId = attempt || crypto.randomUUID();
    setAttempt(attemptId);
    setBusy(true);
    setError("");
    try {
      const response = await fetch("/api/v1/billing/api-credits/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount_usd: amount, attempt_id: attemptId }),
      });
      const body = await response.json();
      if (!response.ok) {
        if (
          ["topup_attempt_conflict", "topup_already_paid"].includes(
            body?.error?.code,
          )
        )
          setAttempt(null);
        throw new Error(
          body?.error?.message || "Checkout could not be opened. Please retry.",
        );
      }
      window.location.assign(body.url);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Checkout could not be opened. Please retry.",
      );
      setBusy(false);
    }
  }

  return (
    <section
      id="api-credits"
      className="api-key-panel"
      aria-labelledby="api-credits-heading"
    >
      <h2 id="api-credits-heading">API credits</h2>
      <p>
        Both API models cost $5 per million normalized input characters.
        Personal subscriptions are separate.
      </p>
      {balance ? (
        <>
          <dl className="api-usage" aria-label="Prepaid API balance">
            <div>
              <dt>Available USD</dt>
              <dd>${balance.available_usd}</dd>
            </div>
            <div>
              <dt>Reserved for active requests</dt>
              <dd>${balance.reserved_usd}</dd>
            </div>
          </dl>
          {balance.billing_review_required && (
            <p role="alert">
              Your API balance requires billing review. Contact support before
              adding credits or scoring.
            </p>
          )}
          <form className="api-key-form" onSubmit={fund}>
            <label htmlFor="api-credit-amount">Amount to add (USD)</label>
            <input
              id="api-credit-amount"
              type="number"
              step="0.01"
              min={balance.topup_min_usd}
              max={balance.topup_max_usd}
              required
              value={amount}
              disabled={busy}
              onChange={(event) => {
                setAmount(event.target.value);
                setAttempt(null);
              }}
            />
            <button
              className="button primary"
              disabled={busy || balance.billing_review_required}
            >
              {busy ? "Opening Checkout…" : "Add credits with Stripe"}
            </button>
          </form>
          <p className="muted">
            Choose any amount from ${Number(balance.topup_min_usd).toFixed(2)}{" "}
            to ${Number(balance.topup_max_usd).toFixed(2)}. Credits become
            available after payment is confirmed. Returning from Checkout does
            not confirm payment. Applicable tax is shown in Checkout and does
            not become API credit.
          </p>
        </>
      ) : (
        <p>Loading API credit balance…</p>
      )}
      <p className="muted">
        Successful requests, including cached results, use credits. Failed
        requests are free. Idempotent replays do not charge again.
      </p>
      <button className="button" type="button" onClick={() => void refresh()}>
        Refresh API balance
      </button>
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
